import { SITE } from "@/lib/site";
import {
  Truck,
  Zap,
  Wrench,
  PackageOpen,
  Globe2,
  Container,
  Snowflake,
  ArrowUpRight,
} from "lucide-react";

const ICONS = {
  "heavy-haul": Truck,
  hotshot: Zap,
  equipment: Wrench,
  flatbed: PackageOpen,
  "dry-van": Container,
  reefer: Snowflake,
  "cross-border": Globe2,
};

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#0A0A0A] py-24 lg:py-32 border-t border-[#161616]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="h-[2px] w-10 bg-[#1E5BFF]" />
              <span className="font-display text-xs font-bold uppercase tracking-[0.3em] text-[#1E5BFF]">
                What We Move
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase text-white tracking-tighter leading-[0.95]">
              Services Built for
              <br />
              <span className="text-[#1E5BFF]">Heavy Workloads.</span>
            </h2>
          </div>
          <p className="max-w-md text-slate-400 text-base md:text-lg leading-relaxed">
            From a single hotshot run to multi-axle oversized hauls — Coreline delivers with the
            right equipment, permits, and people.
          </p>
        </div>

        {/* Bento-ish grid: 1 large + 4 standard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1a1a1a]">
          {SITE.services.map((svc, idx) => {
            const Icon = ICONS[svc.key] || Truck;
            const isFeatured = idx === 0;
            return (
              <article
                key={svc.key}
                data-testid={`service-card-${svc.key}`}
                className={`card-industrial group relative overflow-hidden p-8 lg:p-10 ${
                  isFeatured ? "md:col-span-2 lg:col-span-2 lg:row-span-2 min-h-[420px]" : "min-h-[260px]"
                }`}
              >
                {isFeatured && (
                  <>
                    <img
                      src={SITE.assets.truckFront}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 transition-opacity"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/70 to-transparent" />
                  </>
                )}

                <div className="relative z-10 h-full flex flex-col">
                  <div className="flex items-start justify-between">
                    <div className="w-14 h-14 flex items-center justify-center border border-[#1E5BFF]/50 bg-[#0d1430]">
                      <Icon size={26} className="text-[#1E5BFF]" />
                    </div>
                    <ArrowUpRight
                      size={22}
                      className="text-white/40 group-hover:text-[#1E5BFF] transition-colors"
                    />
                  </div>

                  <h3
                    className={`font-display font-black uppercase tracking-tight text-white mt-8 ${
                      isFeatured ? "text-4xl lg:text-5xl" : "text-2xl lg:text-3xl"
                    }`}
                  >
                    {svc.title}
                  </h3>

                  <p
                    className={`mt-4 text-slate-400 leading-relaxed ${
                      isFeatured ? "text-base lg:text-lg max-w-lg" : "text-sm"
                    }`}
                  >
                    {svc.blurb}
                  </p>

                  <ul className={`mt-auto pt-6 flex flex-wrap gap-x-5 gap-y-2`}>
                    {svc.points.map((p) => (
                      <li
                        key={p}
                        className="font-display text-[11px] font-bold uppercase tracking-[0.2em] text-white/70 flex items-center gap-2"
                      >
                        <span className="w-1.5 h-1.5 bg-[#1E5BFF]" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
